import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { type Dispatch, type SetStateAction, useCallback, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MatchingOperator } from '../../constants';
import type { AudienceForm } from '../../types';
import { hasAnySegments } from '../../utils/validateNormalizedDefinition';
import { MarketUnionAppliedInfoModal } from './components/drawer/components/MarketUnionAppliedInfoModal';
import { SegmentsSelectionDrawer } from './components/drawer/SegmentsSelectionDrawer';
import { ExcludesMarketConflictModal } from './components/ExcludesMarketConflictModal';
import type { DrawerState } from './drawerTypes';
import { ExcludeCriteria } from './ExcludeCriteria';
import { useCriteriaErrors } from './hooks/useCriteriaErrors';
import { useIncludesMarketBlockExcludes } from './hooks/useIncludesMarketBlockExcludes';
import { IncludeCriteria } from './IncludeCriteria';

type CriteriaProps = {
  showExcludeSection: boolean;
  isEdit: boolean;
  isCreate?: boolean;
  isCreateFromTemplate?: boolean;
  setShowExcludeSection: Dispatch<SetStateAction<boolean>>;
};

export const Criteria = ({
  showExcludeSection,
  setShowExcludeSection,
  isEdit,
  isCreate,
  isCreateFromTemplate,
}: CriteriaProps) => {
  const { t } = useTranslation();
  const { setValue } = useFormContext<AudienceForm>();
  const [drawerState, setDrawerState] = useState<DrawerState>({ isOpen: false });
  const [marketUnionInfoOpen, setMarketUnionInfoOpen] = useState(false);
  const { includesError, excludesError } = useCriteriaErrors();

  const { blockReason } = useIncludesMarketBlockExcludes({ isCreate, isCreateFromTemplate });

  const excludesGroups = useWatch<AudienceForm, 'normalized_definition.excludes.groups'>({
    name: 'normalized_definition.excludes.groups',
  });

  const excludesHasSegments = hasAnySegments(excludesGroups ?? []);
  const excludesMarketBlocks = blockReason != null;
  const shouldShowExcludesConflictModal = excludesMarketBlocks && excludesHasSegments;

  const [excludesConflictModalOpen, setExcludesConflictModalOpen] = useState(false);

  useEffect(() => {
    setExcludesConflictModalOpen(shouldShowExcludesConflictModal);
  }, [shouldShowExcludesConflictModal]);

  const handleRemoveExcludesFromConflictModal = useCallback(() => {
    setValue(
      'normalized_definition.excludes',
      { operator: MatchingOperator.UNION, groups: [] },
      { shouldValidate: true, shouldDirty: true }
    );
    setShowExcludeSection(false);
    setExcludesConflictModalOpen(false);
  }, [setValue, setShowExcludeSection]);

  const excludesMarketBlockUserMessage = t(
    'Exclusions are not available while include groups use cross-region targeting or sellable multipartner (Data Marketplace) segments on non-US markets.'
  );

  const onMarketUnionCoerced = useCallback(() => {
    setMarketUnionInfoOpen(prev => (prev ? prev : true));
  }, []);

  const onMarketUnionInfoModalClose = useCallback(() => {
    setMarketUnionInfoOpen(false);
  }, []);

  const hideExclusionSection = () => {
    setShowExcludeSection(false);
  };

  return (
    <Box data-test="criteria-container">
      <ExcludesMarketConflictModal
        isOpen={excludesConflictModalOpen}
        onRemoveExcludes={handleRemoveExcludesFromConflictModal}
      />
      <MarketUnionAppliedInfoModal isOpen={marketUnionInfoOpen} onClose={onMarketUnionInfoModalClose} />

      {drawerState.isOpen && (
        <SegmentsSelectionDrawer
          currentDrawerType={drawerState.type}
          drawerState={drawerState}
          setDrawerState={setDrawerState}
          isCreate={isCreate}
          isCreateFromTemplate={isCreateFromTemplate}
          onMarketUnionCoerced={onMarketUnionCoerced}
        />
      )}

      <Stack sx={{ gap: 4 }}>
        <IncludeCriteria
          isEdit={isEdit}
          isCreate={isCreate}
          isCreateFromTemplate={isCreateFromTemplate}
          errorMessage={typeof includesError?.message === 'string' ? includesError?.message : ''}
          setDrawerState={setDrawerState}
          onAddExclusion={setShowExcludeSection}
          onMarketUnionCoerced={onMarketUnionCoerced}
          isAddExclusionBlocked={excludesMarketBlocks}
          addExclusionBlockedReason={excludesMarketBlockUserMessage}
        />

        {showExcludeSection && (
          <ExcludeCriteria
            isEdit={isEdit}
            isCreate={isCreate}
            isCreateFromTemplate={isCreateFromTemplate}
            onRemove={hideExclusionSection}
            errorMessage={typeof excludesError?.message === 'string' ? excludesError?.message : ''}
            setDrawerState={setDrawerState}
            onMarketUnionCoerced={onMarketUnionCoerced}
            isListInteractionDisabled={excludesMarketBlocks}
            listInteractionDisabledMessage={
              excludesMarketBlocks && !excludesHasSegments ? excludesMarketBlockUserMessage : undefined
            }
          />
        )}
      </Stack>
    </Box>
  );
};
